import type { ProjectSettings, CornerKey } from '$lib/model/types';

// Burn registration marks into a 1-bit RGBA mask (BLACK = ink).
// Returns a new ImageData; the input is not modified.
//
// Mark style is currently always "cross" in v1 (the only non-`none` option
// implemented). Geometry comes from the project settings: `mark_size_in`,
// `mark_margin_in`, `mark_corners`, `mark_centerlines`. Sizes are converted
// from inches using the project DPI to pixels.
//
// The function operates at the output canvas dimensions (`width`, `height`)
// — i.e. the layer's final exported pixel size.

export function compositeRegMarks(
  mask: ImageData,
  settings: ProjectSettings,
): ImageData {
  if (settings.reg_marks === 'none') return mask;
  const out = new ImageData(
    new Uint8ClampedArray(mask.data),
    mask.width,
    mask.height,
  );
  const w = mask.width;
  const h = mask.height;
  const dpi = settings.dpi;
  const sizePx = Math.max(2, Math.round(settings.mark_size_in * dpi));
  const marginPx = Math.max(0, Math.round(settings.mark_margin_in * dpi));
  const armPx = Math.round(sizePx / 2);
  const thicknessPx = Math.max(1, Math.round(dpi * 0.01)); // ~0.01 in stroke

  for (const corner of settings.mark_corners) {
    const { cx, cy } = cornerCenter(corner, w, h, marginPx, armPx);
    drawCross(out, cx, cy, armPx, thicknessPx);
  }

  if (settings.mark_centerlines) {
    drawCross(out, w / 2, marginPx + armPx, armPx, thicknessPx);
    drawCross(out, w / 2, h - marginPx - armPx, armPx, thicknessPx);
    drawCross(out, marginPx + armPx, h / 2, armPx, thicknessPx);
    drawCross(out, w - marginPx - armPx, h / 2, armPx, thicknessPx);
  }

  return out;
}

function cornerCenter(
  corner: CornerKey,
  w: number,
  h: number,
  margin: number,
  arm: number,
): { cx: number; cy: number } {
  switch (corner) {
    case 'TL':
      return { cx: margin + arm, cy: margin + arm };
    case 'TR':
      return { cx: w - margin - arm, cy: margin + arm };
    case 'BL':
      return { cx: margin + arm, cy: h - margin - arm };
    case 'BR':
      return { cx: w - margin - arm, cy: h - margin - arm };
  }
}

function drawCross(
  img: ImageData,
  cx: number,
  cy: number,
  arm: number,
  thickness: number,
): void {
  // Horizontal stroke
  drawRect(
    img,
    Math.round(cx - arm),
    Math.round(cy - thickness / 2),
    Math.round(arm * 2),
    thickness,
  );
  // Vertical stroke
  drawRect(
    img,
    Math.round(cx - thickness / 2),
    Math.round(cy - arm),
    thickness,
    Math.round(arm * 2),
  );
}

function drawRect(
  img: ImageData,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const W = img.width;
  const H = img.height;
  const d = img.data;
  const x0 = Math.max(0, x);
  const y0 = Math.max(0, y);
  const x1 = Math.min(W, x + w);
  const y1 = Math.min(H, y + h);
  for (let yy = y0; yy < y1; yy++) {
    for (let xx = x0; xx < x1; xx++) {
      const i = (yy * W + xx) * 4;
      d[i] = 0;
      d[i + 1] = 0;
      d[i + 2] = 0;
      d[i + 3] = 255;
    }
  }
}
